import { useEffect, useState } from "react";
import axios from "axios";
import styles from "./BookSearch.module.css"
import { useNavigate } from "react-router-dom";
import SiteInfoFooter from '../components/SiteInfoFooter';

// Constant to manage UI pagination (though currently fetching 100 results from API)
const BOOKS_PER_PAGE = 10;

export default function BookSearch() {
  // State for search input fields
  const [isbn, setIsbn] = useState("");
  const [title, setTitle] = useState("");
  const [author, setAuthor] = useState("");

  // State for storing search results and pagination index
  const [results, setResults] = useState([]);
  const [page, setPage] = useState(0);

  // State to store logged-in user data
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  // On component mount, verify authentication
  useEffect(() => {
    const loadUser = async () => {
      try {
        const res = await fetch("/api/currentUser", {
          credentials: "include", // Required to send the HttpOnly JWT cookie
        });

        const data = await res.json();

        if (!res.ok) {
          throw new Error(data.error || "Not authenticated");
        }

        setUser(data);
      } catch (err) {
        console.error(err);
        // Redirect to login if token is missing or invalid
        navigate("/login");
      }
    };

    loadUser();
  }, [navigate]);

  /**
   * Sends a book to the backend database.
   * Extracts the unique cover ID from the OpenLibrary URL string.
   */
  async function addBook(book) {
    try {
      await axios.post(
        "/api/userBooks",
        {
          isbn: book.isbn === "null" ? null : book.isbn,
          // Parses "https://.../b/id/123-M.jpg" to get "123"
          cover_id: book.coverUrl.split("/b/id/")[1].split("-")[0],
          title: book.title,
          author: book.author,
        },
        { withCredentials: true } // Ensures the session cookie is sent with the request
      );

      alert("Book added");
    } catch (err) {
      if (err.response?.status === 409) {
        alert("Book already added");
      } else {
        console.error(err);
        alert("Failed to add book");
      }
    }
  }

  /**
   * Resolves author keys (e.g., /authors/OL123A) into readable names.
   * Uses Promise.all to fetch multiple authors in parallel.
   */
  async function getAuthorNames(authorsArray) {
    try {
      const names = await Promise.all(
        authorsArray.map(async (a) => {
          const res = await axios.get(
            `https://openlibrary.org${a.key}.json`
          );
          return res.data.name;
        })
      );

      return names.join(", ");
    } catch {
      return "Unknown Author";
    }
  }

  /**
   * Primary search logic.
   * Handles two flows: 
   * 1. Direct ISBN lookup (fast, single result)
   * 2. General Query (Title/Author keywords)
   */
  async function searchForBooks() {
    try {
      // FLOW 1: ISBN DIRECT LOOKUP
      // If ONLY the ISBN field is filled, bypass the general search API
      if (isbn.trim() && !title.trim() && !author.trim()) {
        const cleanIsbn = isbn.trim();
        const coverSize = "M";
        const coverUrl = `https://covers.openlibrary.org/b/isbn/${cleanIsbn}-${coverSize}.jpg`;

        const res = await axios.get(
          `https://openlibrary.org/isbn/${cleanIsbn}.json`
        );

        const data = res.data;

        const bookResult = [{
          title: data.title || "Unknown Title",
          author: (data.authors?.length)
            ? await getAuthorNames(data.authors)
            : "Unknown Author",
          coverUrl,
          isbn: cleanIsbn
        }];

        setResults(bookResult);
        setPage(0);
        return;
      }

      // FLOW 2: GENERAL SEARCH
      // Build query string using URLSearchParams for proper encoding
      const params = new URLSearchParams();

      if (isbn.trim()) {
        params.append("isbn", isbn.trim());
      } else {
        const query = `${title} ${author}`.trim();
        if (query) {
          params.append("q", query);
        }
      }

      // Optimization: request specific fields to reduce payload size
      params.append("fields", "cover_i,title,author_name,isbn");
      params.append("limit", "100");


      // params.toString() converts URLSearchParams into a query string
      const res = await axios.get(
        `https://openlibrary.org/search.json?${params.toString()}`
      );

      // If docs is undefined or null, fall back to an empty array
      const covers = (res.data.docs || [])

        // Only keep books that have a cover image ID
        // cover_i is required to generate a valid cover image URL
        .filter(doc => doc.cover_i)

        // Transform each book object into a cleaner format
        .map(doc => ({
          // Book title
          title: doc.title,

          // Join multiple author names into a single string
          // If author_name is missing, fall back to an empty array
          author: (doc.author_name || []).join(", "),

          // Build the Open Library cover image URL using the cover ID
          // -M indicates medium-sized image there is also -S and -L
          coverUrl: `https://covers.openlibrary.org/b/id/${doc.cover_i}-M.jpg`,

          // Safely grab the first ISBN if it exists
          // Optional chaining (?.) prevents runtime errors
          // If no ISBN exists, store null
          isbn: doc.isbn?.[0] ?? null
        }));

      // Update React state with the formatted book results
      setResults(covers);

      setPage(0); // reset to first page on new search


    } catch (err) {
      console.error(err);
      alert("Search failed");
    }

  }

  const totalPages = Math.ceil(results.length / BOOKS_PER_PAGE);

  const visibleBooks = results.slice(
    page * BOOKS_PER_PAGE,
    (page + 1) * BOOKS_PER_PAGE
  );

  function nextPage() {
    setPage(prev => (prev + 1) % totalPages);
  }

  function prevPage() {
    setPage(prev => (prev - 1 + totalPages) % totalPages);
  }


  if (!user) {
    return <p>Loading user...</p>;
  }

  return (
    <>
      <div className={styles.container}>

        <h1 className={styles.title}>Search Books</h1>

        <div className={styles.searchCard}>

          <div className={styles.inputBox}>
            {/* Title input field */}
            <input className={`${styles.textInput}`}
              placeholder="Title"

              // Value comes from React state
              value={title}

              // Update the title state whenever the user types
              onChange={e => setTitle(e.target.value)}
            />

            {/* Author input field */}
            <input className={`${styles.textInput}`}
              placeholder="Author"

              // Author state
              value={author}

              // Update the author state on every keystroke
              onChange={e => setAuthor(e.target.value)}
            />
            {/* ISBN field */}
            <input className={`${styles.textInput}`}
              placeholder="ISBN"
              value={isbn}
              onChange={e => setIsbn(e.target.value)}
            />
          </div>

          {/* Button that triggers the Open Library search */}
          <button className={`${styles.buttonClass}`}
            // When clicked, runs the async search function
            onClick={searchForBooks}
          >
            Search
          </button>

          {/* Pagination controls TOP */}
          {results.length > BOOKS_PER_PAGE && (
            <div className={styles.pagination}>
              <button onClick={prevPage}>Previous</button>

              <span>
                Page {page + 1} of {totalPages}
              </span>

              <button onClick={nextPage}>Next</button>
            </div>
          )}

          {visibleBooks.length > 0 && (
            /* List container for search results */
            <ul className={styles.bookCoverGrid}>
              {/* Loop over the results array and render one <li> per book */}
              {visibleBooks
                .filter(book => book.isbn)
                .map((book, i) => (
                  <li className={styles.bookCard} key={i}>
                    <img
                      className={styles.coverImage}
                      src={book.coverUrl}
                      alt={book.title}
                    />

                    <p className={styles.bookInfo}>
                      <h5><strong>{book.title}</strong></h5>
                    </p>

                    <p className={styles.bookInfo}>
                      Author: {book.author}
                    </p>

                    <p className={styles.bookInfo}>
                      ISBN: {book.isbn}
                    </p>

                    <button
                      className={`${styles.buttonClass} ${styles.addButton}`}
                      onClick={() => addBook(book)}
                    >
                      Add
                    </button>
                  </li>
                ))}
            </ul>
          )}

          {/* Pagination controls BOTTOM */}
          {results.length > BOOKS_PER_PAGE && (
            <div className={styles.pagination}>
              <button onClick={prevPage}>Previous</button>

              <span>
                Page {page + 1} of {totalPages}
              </span>

              <button onClick={nextPage}>Next</button>
            </div>
          )}
        </div>

        <button
          className={styles.buttonClass}
          onClick={() => navigate("/userPage_Protected")}
        >
          Back to User Page
        </button>
      </div>
      <SiteInfoFooter />
    </>
  );
}
